import React from 'react';
import ProfileAppealModal from './ProfileAppealModal';

const TONES = {
    warn: { bar: 'bg-amber-50 border-amber-200', label: 'text-amber-700', dot: 'bg-amber-400' },
    info: { bar: 'bg-secondary/5 border-secondary/20', label: 'text-secondary', dot: 'bg-secondary' },
    paused: { bar: 'bg-red-50 border-red-200', label: 'text-red-700', dot: 'bg-red-500' },
};

/**
 * One-line status bar under the profile builder's progress strip:
 * not live yet / under review / changes requested, what's left, and the next
 * action (submit for approval or view feedback). Replaces the old sidebar
 * status card. A live profile only gets a bar while "Open to Work" is off.
 */
const ProfileStatusBar = ({ talentUser, progress, onSubmitForApproval, onTurnOnOpenToWork, disabled = false }) => {
    if (!talentUser?.isApplicationApproved?.()) return null;

    const remaining = progress?.requiredRemaining || [];
    let state;

    if (talentUser.isProfileInProgress()) {
        state = {
            tone: 'warn', label: 'Not live yet',
            message: remaining.length === 1
                ? `Finish ${remaining[0].title} to submit`
                : `Finish ${remaining.length} more sections to submit`,
            submit: false,
        };
    } else if (talentUser.isProfileReadyForApproval()) {
        state = {
            tone: 'warn', label: 'Not live yet',
            message: `All ${progress?.requiredCount || ''} required sections done — submit for approval`,
            submit: true,
        };
    } else if (talentUser.isPendingProfileApproved()) {
        state = { tone: 'info', label: 'Under review', message: 'Wripple reviews within 1–2 business days. Changes you make are included.' };
    } else if (talentUser.isProfileApproved()) {
        if (talentUser.is_available) return null;
        state = { tone: 'paused', label: 'Paused', message: "Clients can't see you", openToWork: true };
    } else if (talentUser.isProfilePendingAppeal()) {
        // Checked before "rejected": a rejected profile keeps that flag while its appeal is pending
        state = { tone: 'info', label: 'Appeal under review', message: 'A talent manager will review your appeal and reach out via email.' };
    } else if (talentUser.isProfileAppealRejected() || talentUser.isProfileRejected()) {
        state = { tone: 'warn', label: 'Changes requested', message: "Update your profile using Wripple's feedback.", feedback: true };
    }

    if (!state) return null;
    const tone = TONES[state.tone];

    return (
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2 md:px-8 ${tone.bar}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${tone.dot}`} />
            <span className={`text-[11px] font-bold uppercase tracking-wider ${tone.label}`}>{state.label}</span>
            {/* Phones: message drops to its own line under the label + action */}
            <span className="order-last w-full sm:order-none sm:w-auto grow shrink sm:basis-0 min-w-0 text-xs md:text-sm text-gray-600">{state.message}</span>

            {state.submit !== undefined && (
                <button
                    type="button"
                    disabled={disabled || !state.submit}
                    onClick={onSubmitForApproval}
                    className="ml-auto btn btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                >
                    Submit for approval →
                </button>
            )}
            {state.openToWork && (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onTurnOnOpenToWork}
                    className="ml-auto btn btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                >
                    Turn on Open to Work
                </button>
            )}
            {state.feedback && (
                <ProfileAppealModal
                    isAppeal={talentUser.isProfileAppealRejected()}
                    talentUser={talentUser}
                    buttonClassName="ml-auto btn btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                />
            )}
        </div>
    );
};

export default ProfileStatusBar;
